import { useState, useCallback, useEffect } from "react";
import { useAppData } from "./hooks/useAppData";
import { useInactivityTimeout } from "./hooks/useInactivityTimeout";
import { btnPrimary } from "./components/shared";
import LandingPage from "./components/LandingPage";
import AppShell from "./components/AppShell";
import AcceptInvite from "./components/AcceptInvite";
import TenantSetup from "./components/TenantSetup";
import ResetPassword from "./components/ResetPassword";
import TenantPortal from "./components/TenantPortal";
import AdminPanel from "./components/AdminPanel";
import Pricing from "./components/Pricing";
import Features from "./components/Features";
import CookiePolicy from "./components/CookiePolicy";
import PrivacyPolicy from "./components/PrivacyPolicy";
import Onboarding from "./components/Onboarding";
import LeaseSign from "./components/LeaseSign";
import { supabase } from "./supabaseClient";

// Capture URL state once at module load — before React or useAppData can modify it.
const _initHash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
const _initPath = window.location.pathname;

// Admin panel: detect by path — AdminPanel manages its own session/password gate.
const IS_ADMIN_PAGE   = _initPath === "/admin"   || _initPath.startsWith("/admin/");
const PLATFORM_ADMIN_EMAIL = "brianna@leaslypro.com";
const IS_PRICING_PAGE = _initPath === "/pricing" || _initPath.startsWith("/pricing");

// Features page: public, no auth needed — same pattern as /pricing.
const IS_FEATURES_PAGE = _initPath === "/features" || _initPath.startsWith("/features");

// Cookie policy: public, no auth needed — same pattern as /pricing.
const IS_COOKIE_POLICY_PAGE = _initPath === "/cookie-policy" || _initPath.startsWith("/cookie-policy");

// Privacy policy: public, no auth needed — same pattern as /cookie-policy.
const IS_PRIVACY_POLICY_PAGE = _initPath === "/privacy-policy" || _initPath.startsWith("/privacy-policy");

// Tenant setup: detect by path alone — TenantSetup manages its own auth internally.
const IS_TENANT_SETUP_PAGE = _initPath.includes("tenant-setup");

// Lease e-signature: detect by path alone — public, no session required at all.
// Auth is entirely via the ?token= sign_token, verified inside sign-lease.
const IS_SIGN_LEASE_PAGE = _initPath.includes("sign-lease");

// Password recovery: type=recovery means the user clicked a reset-password email link.
const IS_RECOVERY_FLOW = _initHash.get("type") === "recovery";

// Team member invite: type=invite in hash, not on the tenant-setup path.
const IS_INVITE_FLOW   = _initHash.get("type") === "invite";
const IS_ACCEPT_INVITE = IS_INVITE_FLOW && !IS_TENANT_SETUP_PAGE;

function AuthSplash() {
  return (
    <div style={{ minHeight: "100vh", background: "#163C2F", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <style>{`@keyframes leasly-pulse { 0%,100% { opacity:1; transform:scale(1); } 50% { opacity:0.65; transform:scale(0.93); } }`}</style>
      <img src="/leasly-logo.png" alt="Leasly" width="64" height="64" style={{ borderRadius: 14, animation: "leasly-pulse 2s ease-in-out infinite" }} />
    </div>
  );
}

// Moved here from AppShell.jsx — AppShell unmounts/remounts on every
// data.loading flip (every add/edit/delete anywhere in the app calls
// reload(), which is loadAll(), which toggles loading), which was tearing
// down and restarting the inactivity clock each time. Living here in App()
// instead, the clock survives those swaps since App() itself never remounts.
function InactivityWarningModal({ countdown, onStayLoggedIn, onLogoutNow }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: "#fff", borderRadius: 16, padding: "36px 40px", maxWidth: 420, width: "100%", boxShadow: "0 12px 48px rgba(0,0,0,0.18)", textAlign: "center" }}>
        <div style={{ fontSize: 36, marginBottom: 16 }}>⏱️</div>
        <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, fontSize: 24, color: "#163C2F", margin: "0 0 10px" }}>
          Still there?
        </h2>
        <p style={{ fontSize: 14, color: "#6b7c6b", lineHeight: 1.7, margin: "0 0 6px" }}>
          You'll be logged out due to inactivity. Click below to stay signed in.
        </p>
        <div style={{ fontFamily: "'Manrope', sans-serif", fontSize: 32, fontWeight: 800, color: countdown <= 30 ? "#dc2626" : "#163C2F", margin: "0 0 24px", letterSpacing: "-0.02em" }}>
          {Math.floor(countdown / 60)}:{String(countdown % 60).padStart(2, "0")}
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
          <button
            onClick={onStayLoggedIn}
            style={{ background: "#163C2F", color: "#F5F1EA", border: "none", borderRadius: 999, padding: "12px 28px", fontFamily: "'Manrope', sans-serif", fontSize: 13, fontWeight: 700, cursor: "pointer", textTransform: "uppercase", letterSpacing: "0.08em" }}
          >
            Stay Logged In
          </button>
          <button
            onClick={onLogoutNow}
            style={{ background: "transparent", color: "#9B9690", border: "1.5px solid #D4CFC7", borderRadius: 999, padding: "12px 24px", fontFamily: "'Manrope', sans-serif", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
          >
            Sign Out Now
          </button>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const data = useAppData(supabase);
  const [showOnboarding, setShowOnboarding] = useState(true);

  // ── Inactivity timeout ─────────────────────────────────────────────────────
  // Lives here (not inside AppShell) so it survives data.loading swaps — see
  // the comment on InactivityWarningModal above. Called unconditionally, before
  // any early return, per the Rules of Hooks; `enabled` gates it to only the
  // authenticated landlord/team-member session (never tenant portal, admin
  // panel, or logged-out pages).
  const isLandlordSession = !!data.session
    && !data.authLoading
    && data.session.user?.user_metadata?.role !== "tenant"
    && data.session.user?.email !== PLATFORM_ADMIN_EMAIL;

  const handleInactivityLogout = useCallback(async () => {
    await supabase.auth.signOut();
    // Pass reason so the sign-in page can show a contextual message
    window.location.replace("/app?signin&reason=inactivity");
  }, []);

  const { showWarning, stayLoggedIn } = useInactivityTimeout({
    enabled:  isLandlordSession,
    onLogout: handleInactivityLogout,
  });

  // Countdown displayed in the warning modal (counts down from 120 seconds)
  const [countdown, setCountdown] = useState(120);
  useEffect(() => {
    if (!showWarning) { setCountdown(120); return; }
    const iv = setInterval(() => setCountdown(s => Math.max(0, s - 1)), 1000);
    return () => clearInterval(iv);
  }, [showWarning]);

  // 0a. Pricing page — public, no auth needed.
  if (IS_PRICING_PAGE) return <Pricing />;

  // 0a1. Features page — public, no auth needed.
  if (IS_FEATURES_PAGE) return <Features />;

  // 0a2. Cookie policy — public, no auth needed.
  if (IS_COOKIE_POLICY_PAGE) return <CookiePolicy />;

  // 0a3. Privacy policy — public, no auth needed.
  if (IS_PRIVACY_POLICY_PAGE) return <PrivacyPolicy />;

  // 0b. Admin panel — rendered immediately, manages its own session + password gate.
  if (IS_ADMIN_PAGE) return <AdminPanel supabase={supabase} />;

  // 0c. Lease e-signature page — rendered immediately, no auth/session involved.
  if (IS_SIGN_LEASE_PAGE) {
    return <LeaseSign supabase={supabase} />;
  }

  // 1. Tenant setup page — rendered immediately, manages its own auth state.
  if (IS_TENANT_SETUP_PAGE) {
    return <TenantSetup supabase={supabase} />;
  }

  // 2. Password recovery — must intercept before the normal session check so the
  //    user is shown the reset form instead of being dropped into the dashboard.
  //    ResetPassword manages its own auth via onAuthStateChange(PASSWORD_RECOVERY).
  if (IS_RECOVERY_FLOW) {
    return <ResetPassword supabase={supabase} />;
  }

  // 3. Waiting for Supabase to resolve the session
  if (data.authLoading) return <AuthSplash />;

  // 4. Team member invite acceptance (→ /accept-invite)
  if (IS_ACCEPT_INVITE) {
    if (!data.session) return <LandingPage supabase={supabase} />;
    return <AcceptInvite supabase={supabase} session={data.session} />;
  }

  // 4. Tenant user — show the tenant portal instead of the landlord dashboard
  if (data.session?.user?.user_metadata?.role === "tenant") {
    if (data.loading) return <AuthSplash />;
    return <TenantPortal supabase={supabase} session={data.session} />;
  }

  // 4b. Platform admin — redirect to /admin so the password gate can run.
  //     Does NOT bypass AdminPanel's password check; it only navigates there.
  if (data.session?.user?.email === PLATFORM_ADMIN_EMAIL && !IS_ADMIN_PAGE) {
    window.location.replace("/admin");
    return <AuthSplash />;
  }

  // 5. No session — show marketing/login page
  if (!data.session) return <LandingPage supabase={supabase} />;

  // 5. Authenticated, data still loading
  // 6. Error state
  // 7. New landlord onboarding — show welcome screen if not completed or skipped
  // 8. Normal dashboard
  // Built up as a variable (rather than returned directly from each branch)
  // so the inactivity-warning modal below can wrap all of them — App() itself
  // never unmounts when data.loading flips, so the clock survives every one
  // of these swaps instead of resetting each time.
  let mainContent;
  const cp = data.companyProfile || {};
  const isNewUser = data.properties?.length === 0 && data.tenants?.length === 0;

  if (data.loading) {
    mainContent = (
      <div style={{ minHeight: "100vh", background: "#F5F1EA", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 16 }}>
        <div style={{ textAlign: "center" }}>
          <img src="/leasly-logo.png" alt="Leasly" width="56" height="56" style={{ borderRadius: 12, display: "block", margin: "0 auto 12px" }} />
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, fontSize: 36, color: "#163C2F", letterSpacing: "-0.01em" }}>Leasly</div>
          <div style={{ fontSize: 11, color: "#9B9690", fontFamily: "'Manrope', sans-serif", letterSpacing: "0.1em", textTransform: "uppercase", marginTop: 4 }}>by Gibbs Prodigy Group</div>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <div style={{ width: 28, height: 28, border: "3px solid #D4CFC7", borderTop: "3px solid #163C2F", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        <div style={{ color: "#9B9690", fontSize: 14, fontFamily: "'Manrope', sans-serif" }}>Loading your properties…</div>
      </div>
    );
  } else if (data.error) {
    mainContent = (
      <div style={{ minHeight: "100vh", background: "#F5F1EA", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 16, padding: 32 }}>
        <div style={{ color: "#dc2626", fontWeight: 700, fontSize: 18, fontFamily: "'Manrope', sans-serif" }}>Connection Error</div>
        <div style={{ color: "#9B9690", fontSize: 14, textAlign: "center", maxWidth: 400, fontFamily: "'Manrope', sans-serif" }}>{data.error}</div>
        <button onClick={data.loadAll} style={btnPrimary}>Try Again</button>
      </div>
    );
  } else if (showOnboarding && !cp.onboarding_complete && !cp.onboarding_skipped && isNewUser) {
    mainContent = (
      <Onboarding
        supabase={supabase}
        session={data.session}
        companyProfile={cp}
        onComplete={() => {
          setShowOnboarding(false);
          data.loadAll();
        }}
        onSkip={async () => {
          await supabase.from("company_profiles").upsert(
            { user_id: data.session.user.id, onboarding_skipped: true },
            { onConflict: "user_id" }
          );
          setShowOnboarding(false);
        }}
      />
    );
  } else {
    mainContent = <AppShell supabase={supabase} {...data} />;
  }

  return (
    <>
      {mainContent}
      {showWarning && (
        <InactivityWarningModal
          countdown={countdown}
          onStayLoggedIn={stayLoggedIn}
          onLogoutNow={handleInactivityLogout}
        />
      )}
    </>
  );
}
